/**
 * Realism modes.
 *
 * Grip, brake force, top speed and the solved ideal lap are **identical** in
 * every mode, so braking points never move and what you learn in one mode
 * transfers to the others. A mode only changes what happens once you exceed the
 * limit, and whether an assist stops you exceeding it in the first place.
 *
 * Every `lockedGrip` value below sits inside the range real rubber actually
 * exhibits (roughly 0.70–0.85 of peak). Sim is the harshest *defensible* number,
 * not a licence to model a locked tyre as frictionless.
 */

export type RealismId = 'arcade' | 'sport' | 'sim';

export interface RealismProfile {
  id: RealismId;
  name: string;
  /** One line for the picker. */
  blurb: string;
  /** What actually changes, for the in-game explainer. */
  detail: string[];
  /** Sliding friction as a fraction of peak grip, once the rears let go. */
  lockedGrip: number;
  /**
   * How far past the limit the brakes must go before the rears are *fully*
   * sliding, as a fraction of the limit. Wider means a more progressive
   * breakaway with a bigger window to catch it; a real tyre does not switch
   * from gripping to sliding at a single point either.
   */
  lockProgression: number;
  /** Multiplier on the solid-axle yaw damping in vehicle.ts. Higher is calmer. */
  yawDampScale: number;
  /** Bleed brake pressure to keep combined rear demand inside the grip circle. */
  brakeAssist: boolean;
  /** Corrective yaw moment opposing rear slip angle, the way ESC works. */
  stabilityAssist: number;
}

export const REALISM_MODES: RealismProfile[] = [
  {
    id: 'arcade',
    name: 'Arcade',
    blurb: 'You can’t really spin. Overcook a corner and you run wide instead.',
    detail: [
      'Brake assist bleeds pressure as you turn in, so the rears stay gripping.',
      'Strong stability help catches the back before it comes round.',
      'Locked tyres keep almost all their grip.',
      'Braking points are the same as every other mode — too fast still means too wide.',
    ],
    lockedGrip: 0.92,
    lockProgression: 1.2,
    yawDampScale: 2.0,
    brakeAssist: true,
    stabilityAssist: 1.0,
  },
  {
    id: 'sport',
    name: 'Sport',
    blurb: 'The back steps out progressively. Lift off and you catch it.',
    detail: [
      'No brake assist — braking hard while turned will break the rears loose.',
      'Light stability help, enough to make a slide catchable, not enough to fix it for you.',
      'Locked tyres keep most of their grip, so a slide builds rather than snaps.',
      'Trail braking works and is the fastest way through a corner.',
    ],
    lockedGrip: 0.82,
    lockProgression: 0.7,
    yawDampScale: 1.5,
    brakeAssist: false,
    stabilityAssist: 0.45,
  },
  {
    id: 'sim',
    name: 'Sim',
    blurb: 'Snap oversteer, no help. Mistakes are expensive.',
    detail: [
      'No assists of any kind.',
      'Locked tyres keep the least grip — still realistic, but it lets go quickly.',
      'The kart rotates freely; nothing damps a slide except your own hands.',
    ],
    lockedGrip: 0.72,
    lockProgression: 0.15,
    yawDampScale: 1.0,
    brakeAssist: false,
    stabilityAssist: 0,
  },
];

/**
 * Modes a player can actually pick right now. Sport and Sim are shelved for the
 * time being — their profiles stay in `REALISM_MODES` because the physics
 * contract and `tests/realism.test.ts` are defined across all three, but the
 * pickers and every default only ever see this list. Restore a mode by adding
 * its id back here.
 */
export const ACCESSIBLE_REALISM_IDS: readonly RealismId[] = ['arcade'];

export const ACCESSIBLE_REALISM_MODES: RealismProfile[] = REALISM_MODES.filter((m) =>
  ACCESSIBLE_REALISM_IDS.includes(m.id),
);

export const DEFAULT_REALISM: RealismId = 'arcade';

export function realismById(id: string): RealismProfile {
  return REALISM_MODES.find((m) => m.id === id) ?? REALISM_MODES[0];
}

/** Coerce a stored or inbound realism id to one that is currently pickable. */
export function accessibleRealismId(id: string): RealismId {
  return (ACCESSIBLE_REALISM_IDS as readonly string[]).includes(id)
    ? (id as RealismId)
    : DEFAULT_REALISM;
}
